/**
 * ViyoraSkeleton — branded loading placeholders.
 *
 * Three variants covering the common empty-state shapes on Viyora:
 *   - `row`  : list-item skeleton with avatar + two text lines
 *   - `card` : KPI-card skeleton (label + big number)
 *   - `stat` : same as card but ×4 (dashboard KPI strip)
 *
 * Every skeleton overlays a low-opacity Viyora "V" mark so long loads
 * still feel like Viyora — never a generic grey shimmer. The shimmer
 * itself is driven by a single CSS keyframe declared inline so no
 * global stylesheet edit is needed.
 */
export default function ViyoraSkeleton({ variant = "row", count = 3, className = "" }) {
  const N = Math.max(1, count | 0);
  return (
    <div className={`viyora-skel-wrap ${className}`} data-testid={`viyora-skeleton-${variant}`}>
      {variant === "row"  && Array.from({ length: N }).map((_, i) => <RowSkel key={i} i={i} />)}
      {variant === "card" && <CardSkel />}
      {variant === "stat" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: N }).map((_, i) => <CardSkel key={i} i={i} />)}
        </div>
      )}
      <SkelStyles />
    </div>
  );
}

function RowSkel({ i = 0 }) {
  return (
    <div className="viyora-skel-row relative overflow-hidden flex items-center gap-3 rounded-xl border border-[#E8EAE9] bg-white/70 px-4 py-3"
         style={{ animationDelay: `${i * 80}ms` }}>
      <div className="viyora-skel-bar w-10 h-10 rounded-full shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="viyora-skel-bar h-3 rounded-full" style={{ width: "45%" }} />
        <div className="viyora-skel-bar h-3 rounded-full" style={{ width: "70%" }} />
      </div>
      <VMark />
    </div>
  );
}

function CardSkel({ i = 0 }) {
  return (
    <div className="viyora-skel-row relative overflow-hidden rounded-2xl border border-[#E8EAE9] bg-white/70 p-4"
         style={{ animationDelay: `${i * 90}ms` }}>
      <div className="viyora-skel-bar h-2.5 rounded-full" style={{ width: "40%" }} />
      <div className="viyora-skel-bar h-8 rounded-lg mt-3" style={{ width: "70%" }} />
      <VMark corner />
    </div>
  );
}

/** Subtle Viyora "V" mark — decorative, aria-hidden. */
function VMark({ corner }) {
  return (
    <img
      src="/viyora-icon-256.png"
      alt=""
      aria-hidden="true"
      className={`viyora-skel-mark ${corner ? "corner" : ""}`}
      draggable="false"
    />
  );
}

function SkelStyles() {
  return (
    <style>{`
      .viyora-skel-wrap { position: relative; }
      .viyora-skel-wrap > * + * { margin-top: 8px; }
      .viyora-skel-bar {
        background: linear-gradient(90deg, #F0EBE0 0%, #E8E2D3 40%, #F5F1E7 60%, #F0EBE0 100%);
        background-size: 200% 100%;
        animation: viyora-skel-shimmer 1.6s ease-in-out infinite;
      }
      .viyora-skel-row { animation: viyora-skel-fade .25s ease-out both; }
      .viyora-skel-mark {
        position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
        width: 26px; height: 26px;
        opacity: .06; pointer-events: none;
        filter: grayscale(0.2);
      }
      .viyora-skel-mark.corner {
        right: 10px; top: 10px; transform: none;
        width: 32px; height: 32px; opacity: .05;
      }
      @keyframes viyora-skel-shimmer {
        0%   { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @keyframes viyora-skel-fade {
        from { opacity: 0; transform: translateY(4px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `}</style>
  );
}
